import React from "react";
import {
  StyledModal,
  Title,
  SubTitle,
  Frame,
} from "./styles/confirm";

import { ModalProps } from "antd";
import { scrim } from "src/theme/ui";
import { Button, DialogActions, DialogMark } from "src/components/ui";

interface IConfirmModal extends ModalProps {
  onDelete?: () => void;
  onCancel?: () => void;
  buttonLoading?: boolean;
}

const Confirm = ({
  buttonLoading,
  onDelete,
  onCancel,
  ...rest
}: IConfirmModal) => {
  return (
    <StyledModal
      wrapClassName="fina-dialog"
      maskStyle={{ background: scrim, backdropFilter: "none" }}
      centered
      closable={false}
      /* Clicking the scrim cancels. `onCancel` was pulled out of the props for
         the Cancel button and never given to the dialog, so antd had no
         handler for a mask click and the only way out of this was to hit a
         button. Dismissing a destructive confirmation is the safe direction,
         which is the one a stray click should take. */
      onCancel={onCancel}
      width={480}
      /**
       * One row, the way every other dialog in the product ends.
       *
       * These were two full-width buttons stacked in a column half the band
       * wide, so the pair sat off to one side with the destructive choice ON
       * TOP of the safe one — and the emphasis was inverted with it: deleting
       * was the outlined button and cancelling was the solid blue one, which
       * is the loudest thing on the screen sitting on the action that does
       * nothing.
       *
       * Cancel left and quiet, delete right and solid red: the order the
       * reader is asked to choose in, and weight that matches what each one
       * does. The buttons are the design system's own — the pair here were
       * 48px tall with a 5px corner and their danger colours written out by
       * hand.
       */
      footer={[
        <DialogActions key="customAction">
          <Button variant="secondary" size="lg" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="destructiveSolid"
            size="lg"
            onClick={onDelete}
            loading={buttonLoading}
          >
            Delete
          </Button>
        </DialogActions>,
      ]}
      {...rest}
    >
      <Frame>
        {/* The shared mark, so this dialog and the confirmations it sits
            beside announce themselves the same way. */}
        <DialogMark tone="danger" />
        {/* Sentence case. "Delete this Bank Account?" title-cased two ordinary
            words in the middle of a question. */}
        <Title>Delete this bank account?</Title>
        <SubTitle>
          {/* Was one sentence spliced together with a comma — "will review
              this action, you can cancel it before that" — ending on "before
              that", which leaves the reader working out what "that" was. Two
              sentences, and the thing that can be cancelled is named. */}
          {process.env.REACT_APP_BANK_NAME || ""} Capital has to approve this.
          Until they do, you can still cancel the request.
        </SubTitle>
      </Frame>
    </StyledModal>
  );
};

export default Confirm;
